interface CurrencyConfig {
  code: string;
  symbol: string;
  position: 'before' | 'after';
  rate: number;
  showKlarna: boolean;
  locale: string;
}

export const KLARNA_MAX_EUR_AMOUNT = 2000;
export const KLARNA_INSTALLMENTS = 3;

const KLARNA_COUNTRIES = [
  'DE','AT','BE','DK','ES','FI','FR','IE','IT','NO','NL','PT','GB','SE'
];

const CURRENCIES: Record<string, CurrencyConfig> = {
  FR: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'fr-FR' },
  BE: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'fr-BE' },
  LU: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: false, locale: 'fr-LU' },
  CH: { code: 'CHF', symbol: 'CHF', position: 'after', rate: 0.97, showKlarna: false, locale: 'fr-CH' },
  CA: { code: 'CAD', symbol: '$', position: 'before', rate: 1.47, showKlarna: false, locale: 'fr-CA' },
  US: { code: 'USD', symbol: '$', position: 'before', rate: 1.08, showKlarna: false, locale: 'en-US' },
  GB: { code: 'GBP', symbol: '£', position: 'before', rate: 0.85, showKlarna: true, locale: 'en-GB' },
  AU: { code: 'AUD', symbol: '$', position: 'before', rate: 1.65, showKlarna: false, locale: 'en-AU' },
  NZ: { code: 'NZD', symbol: '$', position: 'before', rate: 1.78, showKlarna: false, locale: 'en-NZ' },
  ZA: { code: 'ZAR', symbol: 'R', position: 'before', rate: 20.5, showKlarna: false, locale: 'en-ZA' },
  MC: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: false, locale: 'fr-MC' },
  DE: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'de-DE' },
  AT: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'de-AT' },
  DK: { code: 'DKK', symbol: 'kr', position: 'after', rate: 7.45, showKlarna: true, locale: 'da-DK' },
  FI: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'fi-FI' },
  IE: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'en-IE' },
  IT: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'it-IT' },
  NO: { code: 'NOK', symbol: 'kr', position: 'after', rate: 11.5, showKlarna: true, locale: 'nb-NO' },
  ES: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'es-ES' },
  PT: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'pt-PT' },
  NL: { code: 'EUR', symbol: '€', position: 'after', rate: 1, showKlarna: true, locale: 'nl-NL' },
  SE: { code: 'SEK', symbol: 'kr', position: 'after', rate: 11.3, showKlarna: true, locale: 'sv-SE' }
};

const EURO_COUNTRIES = [
  'AD', 'CY', 'EE', 'GR', 'HR', 'LV', 'LT', 'MT', 'SM', 'SK', 'SI', 'VA'
];

for (const country of EURO_COUNTRIES) {
  CURRENCIES[country] = {
    code: 'EUR',
    symbol: '€',
    position: 'after',
    rate: 1,
    showKlarna: false,
    locale: 'fr-FR',
  };
}

const COUNTRY_STORAGE_KEY = 'userCountry';
const COUNTRY_PREFERENCE_KEY = 'userCountryPreference';

const readStoredCountry = (key: string) => {
  if (typeof window === 'undefined') return '';
  const country = window.localStorage.getItem(key)?.trim().toUpperCase() || '';
  return CURRENCIES[country] ? country : '';
};

const getBrowserCountry = () => {
  if (typeof navigator === 'undefined') return '';
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];

  for (const language of languages) {
    const country = String(language || '').split('-')[1]?.toUpperCase() || '';
    if (CURRENCIES[country]) return country;
  }

  return '';
};

const getInitialCountry = () =>
  readStoredCountry(COUNTRY_PREFERENCE_KEY) || getBrowserCountry() || 'FR';

const roundAmount = (value: number) => Number((Number(value || 0)).toFixed(2));

const CURRENCY_RATE_BY_CODE = Object.values(CURRENCIES).reduce<Record<string, number>>(
  (accumulator, config) => {
    if (!accumulator[config.code]) {
      accumulator[config.code] = config.rate;
    }

    return accumulator;
  },
  { EUR: 1 }
);

const CURRENCY_LOCALE_BY_CODE = Object.values(CURRENCIES).reduce<Record<string, string>>(
  (accumulator, config) => {
    if (!accumulator[config.code]) {
      accumulator[config.code] = config.locale;
    }

    return accumulator;
  },
  { EUR: 'fr-FR' }
);

class CurrencyService {
  private currentCountry: string = getInitialCountry();
  private subscribers: ((country: string) => void)[] = [];
  private isInitialized = false;

  constructor() {
    this.initializeCountry();

    // **Synchronisation multi-onglets**
    window.addEventListener('storage', (event) => {
      if (
        (event.key === COUNTRY_PREFERENCE_KEY || event.key === COUNTRY_STORAGE_KEY) &&
        event.newValue &&
        CURRENCIES[event.newValue]
      ) {
        this.currentCountry = event.newValue;
        this.notifySubscribers();
      }
    });
  }

  private async initializeCountry() {
    if (this.isInitialized) return;

    const preferredCountry = readStoredCountry(COUNTRY_PREFERENCE_KEY);
    if (preferredCountry) {
      this.currentCountry = preferredCountry;
      this.isInitialized = true;
      this.notifySubscribers();
      return;
    }

    try {
      const geoResponse = await fetch('/api/public/geo', {
        cache: 'no-store',
        credentials: 'same-origin',
      });
      const geoData = await geoResponse.json();

      if (geoResponse.ok && geoData?.country && CURRENCIES[geoData.country]) {
        this.setDetectedCountry(geoData.country);
        this.isInitialized = true;
        return;
      }
    } catch (error) {
      console.warn('IP detection failed', error);
    }

    this.setDetectedCountry(getBrowserCountry() || 'FR');
    this.isInitialized = true;
  }

  private setDetectedCountry(country: string) {
    if (!CURRENCIES[country]) return;
    this.currentCountry = country;
    localStorage.setItem(COUNTRY_STORAGE_KEY, country);
    this.notifySubscribers();
  }

  public setCountry(country: string) {
    if (CURRENCIES[country]) {
      this.currentCountry = country;
      localStorage.setItem(COUNTRY_STORAGE_KEY, country);
      localStorage.setItem(COUNTRY_PREFERENCE_KEY, country);
      this.notifySubscribers();
    }
  }

  public getCountry() {
    return this.currentCountry;
  }

  public getCurrencyConfig(country?: string): CurrencyConfig {
    return CURRENCIES[country || this.currentCountry] || CURRENCIES.FR;
  }

  public getDisplayCurrencyCode(country?: string) {
    return this.getCurrencyConfig(country).code;
  }

  public getSupportedCurrencyCodes() {
    return Array.from(new Set(Object.values(CURRENCIES).map((config) => config.code)));
  }

  public getCurrencyRate(currencyCode: string) {
    return CURRENCY_RATE_BY_CODE[currencyCode.toUpperCase()] || 1;
  }

  public getCurrencyLocale(currencyCode: string, country?: string) {
    if (country && CURRENCIES[country]) {
      return CURRENCIES[country].locale;
    }

    return CURRENCY_LOCALE_BY_CODE[currencyCode.toUpperCase()] || 'fr-FR';
  }

  public formatCurrencyAmount(
    amount: number,
    currencyCode: string,
    options?: { country?: string; display?: 'symbol' | 'code' }
  ) {
    const safeCurrencyCode = currencyCode.toUpperCase();
    const locale = this.getCurrencyLocale(safeCurrencyCode, options?.country);

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: safeCurrencyCode,
      currencyDisplay: options?.display === 'code' ? 'code' : 'symbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(roundAmount(amount));
  }

  public convertAmount(
    amount: number,
    fromCurrencyCode: string,
    toCurrencyCode: string
  ) {
    const safeFromCurrency = fromCurrencyCode.toUpperCase();
    const safeToCurrency = toCurrencyCode.toUpperCase();
    const amountInEUR = Number(amount || 0) / this.getCurrencyRate(safeFromCurrency);

    return roundAmount(amountInEUR * this.getCurrencyRate(safeToCurrency));
  }

  public convertToDisplayAmount(
    amount: number,
    fromCurrencyCode = 'EUR',
    country?: string
  ) {
    return this.convertAmount(
      amount,
      fromCurrencyCode,
      this.getDisplayCurrencyCode(country)
    );
  }

  public formatAmountForDisplay(
    amount: number,
    fromCurrencyCode = 'EUR',
    options?: { country?: string; display?: 'symbol' | 'code' }
  ) {
    const targetCountry = options?.country;
    const displayCurrencyCode = this.getDisplayCurrencyCode(targetCountry);
    const convertedAmount = this.convertToDisplayAmount(
      amount,
      fromCurrencyCode,
      targetCountry
    );

    return this.formatCurrencyAmount(convertedAmount, displayCurrencyCode, {
      country: targetCountry,
      display: options?.display,
    });
  }

  public formatPrice(priceEUR: number) {
    return this.formatAmountForDisplay(priceEUR, 'EUR');
  }

  public convertToEUR(priceInCurrentCurrency: number) {
    return this.convertAmount(
      priceInCurrentCurrency,
      this.getDisplayCurrencyCode(),
      'EUR'
    );
  }

  public convertFromEUR(priceEUR: number) {
    return this.convertAmount(priceEUR, 'EUR', this.getDisplayCurrencyCode());
  }

  public supportsKlarna(country?: string) {
    return KLARNA_COUNTRIES.includes(country || this.currentCountry);
  }

  public canUseKlarna(priceEUR: number, billingType = 'one-time', country?: string) {
    return (
      this.supportsKlarna(country) &&
      billingType === 'one-time' &&
      Number(priceEUR) > 0 &&
      Number(priceEUR) <= KLARNA_MAX_EUR_AMOUNT
    );
  }

  public getKlarnaInstallmentAmount(priceEUR: number) {
    return Number((priceEUR / KLARNA_INSTALLMENTS).toFixed(2));
  }

  public getKlarnaMaxAmountEUR() {
    return KLARNA_MAX_EUR_AMOUNT;
  }

  public getKlarnaInstallments() {
    return KLARNA_INSTALLMENTS;
  }

  public showKlarna() {
    return this.supportsKlarna();
  }

  public subscribe(callback: (country: string) => void) {
    this.subscribers.push(callback);
    if (this.isInitialized) callback(this.currentCountry);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  private notifySubscribers() {
    this.subscribers.forEach(cb => cb(this.currentCountry));
  }
}

export const currencyService = new CurrencyService();
